import React from 'react';
import { observer } from 'mobx-react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, LinearProgress } from '@mui/material';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';

import FolderTreePicker from 'containers/Documents/components/FolderTreePicker';
import { StyledPickerPath } from 'containers/Documents/components/FolderTreePicker/styles';
import { PathValue } from 'containers/Documents/components/MoveToFolderModal/styles';
import { MAX_IMPORT_DOCUMENT_SIZE_LABEL } from 'core/constants';

import { useImportDocument } from './useImportDocument';
import {
  StyledProgressRow,
  StyledProgressMessage,
  StyledCauseText,
  StyledField,
  StyledFieldLabel,
  StyledDropZone,
  StyledDropIcon,
  StyledDropCopy,
  StyledChooseFileLink,
  StyledDropHint,
  StyledDropError,
  StyledChosenFile,
  StyledChosenFileIcon,
  StyledChosenFileName,
  StyledHiddenFileInput,
  StyledHeaderRow,
  StyledHeaderTitle,
  StyledHeaderSubtitle,
  StyledHeaderUploadIcon,
  StyledHeaderDocIcon,
  StyledHeaderErrorIcon,
} from './ImportDocumentModalStyles';

interface ImportDocumentModalProps {
  onClose: () => void;
}

const ImportDocumentModal: React.FC<ImportDocumentModalProps> = ({ onClose }) => {
  const {
    phase,
    cause,
    file,
    fileError,
    dragActive,
    fileRef,
    dropZoneRef,
    destinationFolderId,
    setDestinationFolderId,
    folderTree,
    destinationPath,
    onInputChange,
    onRemoveFile,
    runImport,
    cancel,
  } = useImportDocument(onClose);

  const handleCancel = (): void => {
    cancel();
    onClose();
  };

  const modalHeader =
    phase === 'select' ? (
      <StyledHeaderRow>
        <StyledHeaderUploadIcon />
        <StyledHeaderTitle>Import document</StyledHeaderTitle>
      </StyledHeaderRow>
    ) : phase === 'importing' ? (
      <StyledHeaderRow>
        <StyledHeaderDocIcon />
        <div>
          <StyledHeaderTitle>Importing document</StyledHeaderTitle>
          <StyledHeaderSubtitle>{file?.name}</StyledHeaderSubtitle>
        </div>
      </StyledHeaderRow>
    ) : (
      <StyledHeaderRow>
        <StyledHeaderErrorIcon />
        <div>
          <StyledHeaderTitle>We could not import this document</StyledHeaderTitle>
          <StyledHeaderSubtitle>{file?.name}</StyledHeaderSubtitle>
        </div>
      </StyledHeaderRow>
    );

  return (
    <CustomModal
      openModal
      onClose={handleCancel}
      modalTitle={modalHeader}
      modalWidth="xs"
      cancelButton={
        <Button onClick={handleCancel} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        phase === 'select' ? (
          <Button onClick={runImport} variant="contained" size="large" disabled={!file || !destinationFolderId}>
            Import
          </Button>
        ) : phase === 'failed' ? (
          <Button onClick={runImport} variant="contained" size="large">
            Try again
          </Button>
        ) : undefined
      }
    >
      {phase === 'select' && (
        <Box mt={2}>
          <StyledField>
            <StyledFieldLabel>Word file</StyledFieldLabel>
            <StyledDropZone
              ref={dropZoneRef}
              $dragOver={dragActive}
              data-testid="import-dropzone"
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <StyledChosenFile>
                  <StyledChosenFileIcon fontSize="small" />
                  <StyledChosenFileName>{file.name}</StyledChosenFileName>
                  <IconButton size="small" aria-label="Remove file" onClick={onRemoveFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </StyledChosenFile>
              ) : (
                <>
                  <StyledDropIcon />
                  <StyledDropCopy>
                    Drag a Word file here, or <StyledChooseFileLink>choose file</StyledChooseFileLink>
                  </StyledDropCopy>
                  <StyledDropHint>Up to {MAX_IMPORT_DOCUMENT_SIZE_LABEL}</StyledDropHint>
                </>
              )}
            </StyledDropZone>
            {fileError && <StyledDropError>{fileError}</StyledDropError>}
            <StyledHiddenFileInput
              ref={fileRef}
              type="file"
              accept=".docx"
              onChange={onInputChange}
              data-testid="import-file-input"
            />
          </StyledField>
          <StyledField>
            <StyledFieldLabel>Destination</StyledFieldLabel>
            <FolderTreePicker
              folders={folderTree}
              selectedFolderId={destinationFolderId || null}
              onSelect={(id) => id && setDestinationFolderId(id)}
            />
            {destinationPath && (
              <StyledPickerPath>
                Destination: <PathValue>{destinationPath}</PathValue>
              </StyledPickerPath>
            )}
          </StyledField>
        </Box>
      )}
      {phase === 'importing' && (
        <Box mt={2}>
          <LinearProgress />
          <StyledProgressRow>
            <CircularProgress size={18} thickness={5} />
            <StyledProgressMessage>Importing your document…</StyledProgressMessage>
          </StyledProgressRow>
        </Box>
      )}
      {phase === 'failed' && (
        <Box mt={2}>
          <StyledCauseText>{cause}</StyledCauseText>
        </Box>
      )}
    </CustomModal>
  );
};

export default observer(ImportDocumentModal);
